/*
  SoapRecoveryBanner — recovery UI for interrupted SOAP recordings.

  Renders when there are `soap_sessions` rows with audio segments still
  on disk but no note produced yet. Each row exposes:
    - Client + appointment metadata
    - Number of audio segments + total bytes retained
    - "Retry" → re-run the finalize pipeline (polls until note lands)
    - "Discard" → permanently delete the recording (asks first)

  This is the safety net when Cloudflare 524s, browser crashes, or a
  server restart interrupts a finalize job. Assessment audio is never
  deleted until a note has been successfully persisted, so we can
  always resume.
*/
import { useEffect, useMemo, useState } from "react";
import { useRecording } from "@/lib/recording";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Loader2, AlertTriangle, RotateCw, Trash2 } from "lucide-react";
import { toast } from "sonner";

function bytesHuman(n) {
  if (!n) return "0 B";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export default function SoapRecoveryBanner({ onRestored, filterClientEmail }) {
  const rec = useRecording();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  // Fetch once on mount and whenever the client filter changes.
  // NOTE: intentionally does NOT depend on `rec` — `rec` is a fresh
  // object every RecordingProvider render, and depending on it would
  // cause the banner to refetch every parent re-render (which
  // occasionally cleared the visible list, making it flicker away).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get("/soap-notes/sessions/pending");
        if (!cancelled) setSessions(r.data?.sessions || []);
      } catch {
        if (!cancelled) setSessions([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [filterClientEmail]);

  async function reload() {
    setLoading(true);
    try {
      const r = await api.get("/soap-notes/sessions/pending");
      setSessions(r.data?.sessions || []);
    } finally {
      setLoading(false);
    }
  }

  // Prefer sessions that match the currently-open client. If none match
  // (e.g. alias / case mismatch, or the user opened the wrong client),
  // show ALL pending sessions so the recording can still be recovered.
  const { visible, filteredOut } = useMemo(() => {
    const email = (filterClientEmail || "").toLowerCase().trim();
    if (!email) return { visible: sessions, filteredOut: false };
    const matches = sessions.filter(
      (s) => (s.client_email || "").toLowerCase() === email,
    );
    if (matches.length > 0) return { visible: matches, filteredOut: false };
    return { visible: sessions, filteredOut: sessions.length > 0 };
  }, [sessions, filterClientEmail]);

  if (!loading && visible.length === 0) return null;

  async function retry(sess) {
    setBusyId(sess.id);
    try {
      const res = await rec.retryFinalize(sess.id, {
        extra_context: sess.therapist_notes || "",
        include_sbn: !!sess.sbn_client_id,
        template_id: sess.template_id || null,
      });
      toast.success("SOAP note generated", {
        description: "The interrupted assessment has been recovered.",
      });
      onRestored?.(res);
      await reload();
    } catch (e) {
      toast.error("Retry failed", {
        description: e?.message || "Please try again in a minute.",
      });
    } finally {
      setBusyId(null);
    }
  }

  async function discard(sess) {
    if (!window.confirm(
      `Permanently delete the recording for ${sess.client_name || sess.client_email}?\n\nThis cannot be undone — the audio segments will be removed.`,
    )) return;
    setBusyId(sess.id);
    try {
      await rec.discardPendingSession(sess.id);
      toast.success("Recording discarded");
      await reload();
    } catch (e) {
      toast.error("Delete failed", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div
      className="rounded-md border border-amber-300 bg-amber-50 p-3"
      data-testid="soap-recovery-banner"
    >
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-amber-900">
        <AlertTriangle className="h-3.5 w-3.5" />
        Interrupted assessment recordings
        {loading && <Loader2 className="h-3 w-3 animate-spin ml-1" />}
      </div>
      <p className="text-xs text-amber-800 mt-1">
        {filteredOut
          ? "No pending recording matches this client's email exactly — showing all interrupted recordings so you can pick the right one."
          : "These recordings finished uploading but the SOAP note didn't complete. Retry to generate the note — the audio is safely preserved server-side."}
      </p>
      <ul className="mt-2 divide-y divide-amber-200">
        {visible.map((s) => {
          const busy = busyId === s.id || s.running;
          return (
            <li
              key={s.id}
              className="py-2 flex items-center gap-3 flex-wrap"
              data-testid={`soap-recovery-item-${s.id}`}
            >
              <div className="flex-1 min-w-[220px]">
                <div className="text-sm font-medium text-amber-950">
                  {s.client_name || s.client_email}
                  {s.appointment_type ? (
                    <span className="text-xs font-normal text-amber-700 ml-2">
                      · {s.appointment_type}
                    </span>
                  ) : null}
                </div>
                <div className="text-2xs text-amber-800 font-mono-num">
                  {s.audio?.segments} segments · {bytesHuman(s.audio?.bytes)}
                  {s.error ? (
                    <span className="ml-2 text-red-700">{s.error}</span>
                  ) : null}
                  {s.started_at ? (
                    <span className="ml-2 opacity-70">
                      · {new Date(s.started_at).toLocaleString()}
                    </span>
                  ) : null}
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={() => retry(s)}
                  disabled={busy}
                  className="bg-amber-700 hover:bg-amber-800 text-white h-8"
                  data-testid={`soap-recovery-retry-${s.id}`}
                >
                  {busy ? (
                    <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                  ) : (
                    <RotateCw className="h-3.5 w-3.5 mr-1" />
                  )}
                  {s.running ? "Running…" : busy ? "Working…" : "Retry"}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => discard(s)}
                  disabled={busy}
                  className="border-red-300 text-red-700 hover:bg-red-50 h-8"
                  data-testid={`soap-recovery-discard-${s.id}`}
                >
                  <Trash2 className="h-3.5 w-3.5 mr-1" /> Discard
                </Button>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
